import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import {
  Actions as ActionsIcon,
  Pdf,
  DocsFile,
  Document,
  ImageFile,
} from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type { LibraryFileItem } from "../types/library.types";
import { LibraryShareType } from "../types/library.types";
import { libraryApi } from "../services/library.api";
import AddDocumentModal from "../components/AddDocumentModal";
import ConfirmModal from "@/components/ui/confirm-modal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { LibraryBasePath } from "./LibraryPage";
import styles from "./DocumentsPage.module.scss";

interface DocumentsPageProps {
  basePath?: LibraryBasePath;
}

const FILE_ICON_MAP: Record<
  string,
  React.ComponentType<{ width?: number; height?: number }>
> = {
  pdf: Pdf,
  doc: DocsFile,
  xls: Document,
  txt: Document,
  ppt: Document,
  png: ImageFile,
};

function getFileIcon(fileType: string) {
  const Icon = FILE_ICON_MAP[fileType.toLowerCase()] ?? Document;
  return <Icon width={20} height={20} />;
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({
  basePath = "admin-library",
}) => {
  const prefix = `/dashboard/${basePath}`;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.LIBRARY_CREATE);
  const canDelete = hasPermission(PERMISSIONS.LIBRARY_DELETE);
  const [documents, setDocuments] = useState<LibraryFileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LibraryFileItem | null>(
    null,
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await libraryApi.getItems({
        type: "file",
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
        scope: basePath === "library" ? "client" : "admin",
      });
      const result = res as { data: LibraryFileItem[]; total: number };
      setDocuments(result.data);
      setTotalDocuments(result.total);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, dateFilter, basePath]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSearch = (value: string) => setSearchTerm(value);
  const handleFilterChange = (filters: Record<string, unknown>) => {
    const dateVal = filters.updatedDate;
    setDateFilter(
      dateVal instanceof Date
        ? dateVal.toISOString()
        : (dateVal as string) || null,
    );
  };

  const handleAddDocumentModalClose = () => setAddDocumentModalOpen(false);
  const handleAddDocumentSubmit = async (payload: {
    share: "everyone" | "admins" | "custom";
    uploadedFileIds?: string[];
    files?: File[];
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      await libraryApi.uploadDocuments({
        fileIds: payload.uploadedFileIds?.length
          ? payload.uploadedFileIds
          : undefined,
        files: payload.uploadedFileIds?.length ? undefined : payload.files,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      toast.success(t("library.document.uploaded"));
      handleAddDocumentModalClose();
      fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleDocumentMenuAction = (action: string, doc: LibraryFileItem) => {
    if (action === "download") {
      if (!doc.downloadUrl) {
        toast.error(t("library.download.noUrl"));
        return;
      }
      window.open(doc.downloadUrl, "_blank", "noopener,noreferrer");
    }
    if (action === "delete" && canDelete) {
      setDeleteTarget(doc);
    }
    // TODO: share, edit
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryApi.deleteItem(deleteTarget.id);
      toast.success(t("library.deleteModal.deleted"));
      setDeleteTarget(null);
      fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDropdownToggle = (id: string, open: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: open }));
  };

  const dateFilterField = {
    name: "updatedDate",
    label: t("library.filters.updatedDate"),
    type: "date" as const,
    placeholder: t("library.filters.updatedDatePlaceholder"),
  };

  const documentColumns: TableColumn<LibraryFileItem>[] = [
    {
      header: t("library.documents.cols.name"),
      accessor: (row) => (
        <div className={styles.documentNameCell}>
          <span className={styles.fileIconWrap}>
            {getFileIcon(row.fileType)}
          </span>
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.uploadedBy"),
      accessor: (row) => row.uploadedBy,
    },
    {
      header: t("library.documents.cols.lastUpdated"),
      accessor: (row) => row.lastUpdatedFormatted,
    },
    {
      header: t("library.documents.cols.fileSize"),
      accessor: (row) => row.fileSize,
    },
    {
      header: t("library.documents.cols.sharedWith"),
      accessor: (row) => (
        <div className={styles.sharedWith}>
          <div className={styles.avatarStack}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.avatarCircle} title={`User ${i}`}>
                {i}
              </div>
            ))}
          </div>
          <span className={styles.plusBadge}>
            {row.sharedWithCount != null ? `+${row.sharedWithCount}` : "—"}
          </span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.action"),
      accessor: (row) => {
        const isOpen = openDropdowns[row.id] ?? false;
        return (
          <Popover
            content={
              <div className={styles.menuDropdown}>
                {/* <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => handleDocumentMenuAction("share", row)}
                >
                  {t("library.documents.share")}
                </button> */}
                {basePath !== "library" && (
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => handleDocumentMenuAction("edit", row)}
                  >
                    {t("common.edit")}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => handleDocumentMenuAction("download", row)}
                >
                  {t("common.download")}
                </button>
                {canDelete && basePath !== "library" && (
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => handleDocumentMenuAction("delete", row)}
                  >
                    {t("common.delete")}
                  </button>
                )}
              </div>
            }
            isOpen={isOpen}
            onClose={() => handleDropdownToggle(row.id, false)}
            position="bottom"
          >
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle(row.id, !isOpen);
              }}
            >
              <ActionsIcon width={20} height={20} />
            </button>
          </Popover>
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("library.title")} />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={() => navigate(prefix)}
          >
            {t("library.title")}
          </button>
          <span className={styles.breadcrumbSep}>&gt;</span>
          <span className={styles.breadcrumbCurrent}>
            {t("library.documents.title")}
          </span>
        </div>

        <PageHeader
          searchPlaceholder={t("library.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={handleSearch}
          filters={[dateFilterField]}
          onFilterChange={handleFilterChange}
          onAddNew={canCreate ? () => setAddDocumentModalOpen(true) : undefined}
          addButtonLabel={
            canCreate ? t("library.addDocumentButton") : undefined
          }
          // showViewToggle
          viewMode="list"
        />

        <div className={styles.tableWrap}>
          <Table
            columns={documentColumns}
            data={documents}
            isLoading={loading}
            getRowId={(row) => row.id}
            totalRecords={totalDocuments}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setCurrentPage(1);
            }}
            renderActions={() => null}
            hasVisibleActions={false}
            openDropdowns={openDropdowns}
            contentBasedHeight
            emptyMessage={t("library.documents.noData")}
          />
        </div>

        <AddDocumentModal
          isOpen={addDocumentModalOpen}
          onClose={handleAddDocumentModalClose}
          onSubmit={handleAddDocumentSubmit}
        />

        <ConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title={t("library.deleteModal.title")}
          message={t("library.deleteModal.message")}
          confirmText={t("common.delete")}
          cancelText={t("buttons.cancel")}
          loading={deleteLoading}
          confirmVariant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
